import { GuestType, Price, SeatType } from "@/graphql/types";
import { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import Modal from "../common/modal/modal";
import Button from "../common/button/button";
import GuestInput from "../common/input/guestInput";

type Props = {
  prices: Price[];
  seatTypes: SeatType[];
  guestTypes: GuestType[];
  onSubmit: (prices: Price[]) => Promise<void>;
  onClose: () => void;
  stageId: number;
};

const EditPrice = (props: Props) => {
  const [newValue, setNewValue] = useState([] as Price[]);
  const [invalidList, setInvalidList] = useState(
    {} as { [seatTypeId: number]: { [guestTypeId: number]: boolean } },
  );

  const invalid = useMemo(
    () =>
      Object.keys(invalidList).some((seatTypeId) => {
        const invalidListOfSeatType = invalidList[parseInt(seatTypeId)];
        return Object.keys(invalidListOfSeatType).some(
          (guestTypeId) => invalidListOfSeatType[parseInt(guestTypeId)],
        );
      }),
    [invalidList],
  );

  const onChange = useCallback(
    (price: Price, errorOccurs: boolean) => {
      const guestTypeId = price.guestType;
      const seatTypeId = price.seatType;
      const newPrice = price.price;

      const filteredOldPrices = props.prices.filter(
        (price) =>
          price.guestType === guestTypeId && price.seatType === seatTypeId,
      );
      const oldValue =
        filteredOldPrices.length > 0 ? filteredOldPrices[0].price : -1;

      const alreadyEdited = newValue.some(
        (value) =>
          value.guestType === guestTypeId && value.seatType == seatTypeId,
      );

      const invalidListCopy = { ...invalidList };
      // エラー有無登録
      if (!invalidListCopy[seatTypeId]) {
        invalidListCopy[seatTypeId] = {};
      }
      invalidListCopy[seatTypeId][guestTypeId] = errorOccurs;
      setInvalidList(invalidListCopy);

      if (newPrice === oldValue) {
        // 値の削除
        if (alreadyEdited) {
          const newValueCopy = [...newValue];
          newValueCopy.some((value, index) => {
            if (
              value.guestType === guestTypeId &&
              value.seatType === seatTypeId
            ) {
              newValueCopy.splice(index, 1);
              return true;
            }
            return false
          });
          setNewValue(newValueCopy);
        }
      } else {
        // 値登録
        if (alreadyEdited) {
          const newValueCopy = [...newValue];
          newValueCopy.forEach((value, index) => {
            if (
              value.guestType === guestTypeId &&
              value.seatType === seatTypeId
            ) {
              newValueCopy[index].price = newPrice;
            }
            console.log(newValueCopy);
          });
          setNewValue(newValueCopy);
        } else {
          const newValueCopy = [...newValue];
          newValueCopy.push(price);
          console.log(newValueCopy);
          setNewValue(newValueCopy);
        }
      }
    },
    [invalidList, newValue, props.prices],
  );

  const submit = useCallback(async () => {
    if (invalid) {
      return;
    }
    await props.onSubmit(newValue);
    props.onClose();
  }, [invalid, newValue, props]);

  const priceComponents = useMemo(() => {
    const getPrice = (
      seatTypeId: number,
      guestTypeId: number,
      stageId: number,
    ) => {
      const filteredPrices = props.prices.filter(
        (price) =>
          price.seatType === seatTypeId &&
          price.guestType === guestTypeId &&
          price.stageId === stageId,
      );
      if (filteredPrices.length > 0) {
        return {
          id: filteredPrices[0].id,
          price: filteredPrices[0].price,
        };
      }
      return {
        id: -1,
        price: 0,
      };
    };

    return props.seatTypes.map((seatType, index) => {
      if (seatType.id < 0) {
        return <></>
      }
      return (
        <div className="edit-price-group" key={"edit-price-group-" + index}>
          <div className="edit-price-group-title">{seatType.name}</div>
          <div className="edit-price-group-elements">
            {props.guestTypes.map((guestType, index2) => {
              const targetPrice = getPrice(
                seatType.id,
                guestType.id,
                props.stageId,
              );
              return (
                <div
                  className="edit-price-element"
                  key={"edit-price-element" + index + "-" + index2}
                >
                  <div className="edit-price-element-title">
                    {guestType.name}
                  </div>
                  <div className="edit-price-element-input">
                    <GuestInput
                      id="price-edit-value"
                      defaultValue={targetPrice.price.toString()}
                      onChange={(price: string, errorOccurs) => {
                        onChange(
                          {
                            price: parseInt(price),
                            id: targetPrice.id,
                            guestType: guestType.id,
                            seatType: seatType.id,
                            stageId: props.stageId,
                          } as Price,
                          errorOccurs,
                        );
                      }}
                      error={
                        invalidList[seatType.id]
                          ? invalidList[seatType.id][guestType.id]
                          : false
                      }
                      errorMessage="使用できない数値です"
                      label=""
                      placeholder="価格"
                      literalValidation={/^(0|[1-9]\d*)$/}
                      width={120}
                    />
                    <div>円</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    });
  }, [
    invalidList,
    onChange,
    props.guestTypes,
    props.prices,
    props.seatTypes,
    props.stageId,
  ]);

  const components = useMemo(
    () => (
      <Modal title="チケット料金設定" onClose={props.onClose}>
        <EditPriceStyle>
          <div className="description">
            座席・観客種別ごとのチケット料金を設定してください。
          </div>
          {priceComponents}
          <div className="buttons">
            <div className="button">
              <Button
                label="保存"
                disabled={invalid}
                buttonType="forward"
                width={80}
                onClick={submit}
              />
            </div>
            <div className="button">
              <Button
                label="キャンセル"
                buttonType="back"
                width={120}
                onClick={() => {
                  props.onClose();
                }}
              />
            </div>
          </div>
        </EditPriceStyle>
      </Modal>
    ),
    [invalid, priceComponents, props, submit],
  );

  return components;
};

export default EditPrice;

const EditPriceStyle = styled.div`
  .description {
    margin: 16px auto 16px auto;
  }
  .edit-price-group {
    width: 280px;
    margin: 16px auto 16px auto;
    .edit-price-group-title {
      font-weight: bold;
    }
    .edit-price-group-elements {
      padding-left: 16px;
      .edit-price-element {
        .edit-price-element-title {
        }
        .edit-price-element-input {
          margin: auto;
          width: 160px;
          display: flex;
          justify-content: left;
          align-items: center;
        }
      }
    }
  }
  .buttons {
    width: 300px;
    display: flex;
    justify-content: center;
    .button {
      margin: 0 4px 0 4px;
    }
  }
`;
